import React, { FC } from 'react';

const LOGO_SRC = 'https://cdn.prod.website-files.com/6450de24808d2bcbbb55df1e/6450de24808d2bcb7d55df63_logo.svg';

export type PortalHeaderChipTone = 'brand' | 'grey' | 'green';

export interface PortalHeaderProps {
  title?: string;
  subtitle?: string;
  chip?: string;
  chipTone?: PortalHeaderChipTone;
  progress?: number;
  onBack?: () => void;
}

function chipToneClass(tone: PortalHeaderChipTone): string {
  if (tone === 'green') return 'text-green-700 bg-green-100';
  if (tone === 'grey') return 'text-slate-500 bg-slate-100';
  return 'text-brand bg-blue-100';
}

const BackChevron: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const LogoRow: FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`flex h-portal-header-mobile md:h-portal-header items-center gap-3 px-4 md:px-6 ${className}`}>
    <div className="flex items-center">
      <img
        src={LOGO_SRC}
        alt="Telecare"
        className="h-7 w-auto"
      />
    </div>
    <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
      <span
        className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500"
        aria-hidden
      />
      Secure &amp; Encrypted
    </div>
  </div>
);

const PortalHeader: FC<PortalHeaderProps> = ({
  title,
  subtitle,
  chip,
  chipTone = 'brand',
  progress,
  onBack,
}) => (
  <header className="sticky top-0 z-portal-sticky flex-shrink-0 border-b border-portal-header bg-white">
    <div className="hidden md:block">
      <LogoRow />
    </div>

    {title ? (
      <div className="flex md:hidden flex-col">
        <div className="flex h-portal-header-mobile items-center gap-3 px-4">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              className="flex h-icon-btn w-icon-btn flex-shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-800"
            >
              <BackChevron />
            </button>
          ) : (
            <span className="w-icon-btn flex-shrink-0" aria-hidden />
          )}
          <div className="flex-1 min-w-0">
            <b className="block text-header-title font-bold leading-tight text-slate-900 truncate">
              {title}
            </b>
            {subtitle && (
              <small className="block text-header-sub text-slate-500 truncate">
                {subtitle}
              </small>
            )}
          </div>
          {chip && (
            <span
              className={`flex-shrink-0 text-header-chip font-semibold px-2 py-1 rounded-full whitespace-nowrap ${chipToneClass(chipTone)}`}
            >
              {chip}
            </span>
          )}
        </div>
        {progress !== undefined && (
          <div className="h-progress-bar bg-slate-200">
            <div
              className="h-progress-bar bg-brand transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}
      </div>
    ) : (
      <div className="flex md:hidden">
        <LogoRow className="w-full" />
      </div>
    )}
  </header>
);

export default PortalHeader;
